import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import type { RegisterPayload } from '@/modules/auth/types/auth.types'
import type { VForm } from 'vuetify/components'
import { DOCUMENT_TYPES_OPTIONS } from '@/shared/constants/roles.constants'

interface RegisterFormData {
  nombres: string
  apellidos: string
  tipoDocumento: 'DNI' | 'CE' | 'Pasaporte'
  dni: string
  email: string
  telefono: string
  password: string
  confirmPassword: string
}

type ValidatableField = keyof RegisterFormData

type ValidationRule = (value: string) => boolean | string

export function useRegister() {
  const router = useRouter()
  const authStore = useAuthStore()

  const formRef = ref<VForm | null>(null)

  const formData = ref<RegisterFormData>({
    nombres: '',
    apellidos: '',
    tipoDocumento: 'DNI',
    dni: '',
    email: '',
    telefono: '',
    password: '',
    confirmPassword: '',
  })

  const isFormValid = ref(false)

  const isLoading = ref(false)

  const showPassword = ref(false)

  const showConfirmPassword = ref(false)

  const errorMessage = ref<string>('')

  const documentTypes = computed(() => DOCUMENT_TYPES_OPTIONS)

  const nombresRules: ValidationRule[] = [
    (v: string) => !!v || 'Los nombres son requeridos',
    (v: string) => (v && v.length >= 2) || 'Los nombres deben tener al menos 2 caracteres',
    (v: string) => /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(v) || 'Los nombres solo deben contener letras',
  ]

  const apellidosRules: ValidationRule[] = [
    (v: string) => !!v || 'Los apellidos son requeridos',
    (v: string) => (v && v.length >= 2) || 'Los apellidos deben tener al menos 2 caracteres',
    (v: string) => /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(v) || 'Los apellidos solo deben contener letras',
  ]

  const tipoDocumentoRules: ValidationRule[] = [
    (v: string) => !!v || 'El tipo de documento es requerido',
  ]

  const dniRules: ValidationRule[] = [
    (v: string) => !!v || 'El número de documento es requerido',
    (v: string) => {
      if (formData.value.tipoDocumento === 'DNI') {
        return /^\d{8}$/.test(v) || 'El DNI debe tener 8 dígitos'
      }
      return (v && v.length >= 6) || 'El número de documento debe tener al menos 6 caracteres'
    },
  ]

  const emailRules: ValidationRule[] = [
    (v: string) => !!v || 'El correo electrónico es requerido',
    (v: string) => {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailPattern.test(v) || 'Ingresa un correo electrónico válido'
    },
  ]

  const telefonoRules: ValidationRule[] = [
    (v: string) => !!v || 'El teléfono es requerido',
    (v: string) => /^\d{9}$/.test(v) || 'El teléfono debe tener 9 dígitos',
  ]

  const passwordRules: ValidationRule[] = [
    (v: string) => !!v || 'La contraseña es requerida',
    (v: string) => (v && v.length >= 6) || 'La contraseña debe tener al menos 6 caracteres',
  ]

  const confirmPasswordRules: ValidationRule[] = [
    (v: string) => !!v || 'Debes confirmar la contraseña',
    (v: string) => v === formData.value.password || 'Las contraseñas no coinciden',
  ]

  const togglePassword = (): void => {
    showPassword.value = !showPassword.value
  }

  const toggleConfirmPassword = (): void => {
    showConfirmPassword.value = !showConfirmPassword.value
  }

  const validateField = async (field: ValidatableField): Promise<void> => {
    if (formRef.value) {
      await formRef.value.validate()
    }
  }

  const clearError = (): void => {
    errorMessage.value = ''
    if (authStore.clearError) {
      authStore.clearError()
    }
  }

  const handleGoToLogin = (): void => {
    router.push({ name: 'Login' })
  }

  const handleSubmit = async (): Promise<void> => {
    if (formRef.value) {
      const { valid } = await formRef.value.validate()
      if (!valid) {
        return
      }
    }

    isLoading.value = true
    clearError()

    try {
      const registerData: RegisterPayload = {
        first_name: formData.value.nombres.trim(),
        last_name: formData.value.apellidos.trim(),
        doc_type: formData.value.tipoDocumento,
        doc_number: formData.value.dni.trim(),
        email: formData.value.email.trim().toLowerCase(),
        phone: formData.value.telefono.trim(),
        password: formData.value.password,
      }

      await authStore.register(registerData)

      router.push({ name: 'ApoderadoInicio' })
    } catch (error: any) {
      const serverMessage = error.message || 'Ocurrió un error al crear tu cuenta. Por favor, intenta nuevamente.'
      errorMessage.value = serverMessage

      if (import.meta.env.DEV) {
        console.error('❌ Error al registrarse:', error)
      }
    } finally {
      isLoading.value = false
    }
  }

  return {
    // Referencias
    formRef,
    formData,

    // Estados
    isFormValid,
    isLoading,
    showPassword,
    showConfirmPassword,
    errorMessage,

    // Computed
    documentTypes,

    // Reglas de validación
    nombresRules,
    apellidosRules,
    tipoDocumentoRules,
    dniRules,
    emailRules,
    telefonoRules,
    passwordRules,
    confirmPasswordRules,

    // Métodos de interacción
    handleSubmit,
    togglePassword,
    toggleConfirmPassword,
    validateField,
    clearError,
    handleGoToLogin,
  }
}
